// One version: lifecycle, server-provided actions, workflow stamps and a
// content summary. The raw content editor exists ONLY in development builds
// (import.meta.env.DEV, removed from production bundles — checked by
// scripts/check-dist.mjs); customers never see JSON.
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, call } from "@/api/client";
import { lazy, Suspense, useState } from "react";
import { keys, useFrontDesk } from "@/api/hooks";
import { notifyError } from "../common/notify";
import { formatDateTime, shortId } from "@/lib/format";
import { Callout, Details, PageHeader, Panel, Section } from "@/ui/layout";
import { VersionStateBadge } from "@/components/status";
import { PageError, PageLoading, useTitle, useRouteParams } from "../common/page";
import { useCreative, useCreativeDistributions, useVersion } from "../common/queries";
import { VersionPanel } from "./version-panel";
import { Button } from "@/ui/button";
import type { CreativeVersion } from "@/api/types";

/** Plain-language summary of a version's design (no JSON, no ids). */
function designSummary(v: CreativeVersion): string {
  if (v.schemaVersion === 2) {
    const layouts = Array.isArray(v.content.layouts) ? v.content.layouts.length : 0;
    const elements = Array.isArray(v.content.elements) ? v.content.elements.length : 0;
    return `${String(layouts)} formato${layouts === 1 ? "" : "s"} · ${String(elements)} elemento${elements === 1 ? "" : "s"}.`;
  }
  const components = Array.isArray(v.content.components) ? v.content.components.length : 0;
  return components === 0
    ? "Esta versión aún no tiene diseño."
    : `${String(components)} componente(s) (formato anterior).`;
}

const DevContentEditor = import.meta.env.DEV ? lazy(() => import("./dev-content-editor")) : null;

export function VersionDetailPage() {
  const { orgId, versionId } = useRouteParams("orgId", "versionId");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [restoring, setRestoring] = useState(false);
  const { data: fd } = useFrontDesk(orgId);
  const version = useVersion(orgId, versionId);
  const creative = useCreative(orgId, version.data?.creativeId ?? "");
  const distributions = useCreativeDistributions(orgId, version.data?.creativeId ?? "");
  useTitle(version.data ? `Versión ${String(version.data.versionNumber)}` : undefined);
  if (version.isLoading) return <PageLoading />;
  if (version.error || !version.data) return <PageError error={version.error} orgId={orgId} />;
  const v = version.data;
  const summary = designSummary(v);
  const who = (id: string | null) => (id ? `usuario …${shortId(id)}` : "—");
  const restore = async () => {
    if (restoring) return;
    setRestoring(true);
    try {
      const created = await call(() =>
        api.POST("/orgs/{orgId}/creatives/{creativeId}/versions", {
          params: { path: { orgId, creativeId: v.creativeId } },
          body: { fromVersionId: v.id },
        })
      );
      await queryClient.invalidateQueries({ queryKey: keys.org(orgId) });
      toast.success(`Versión ${String(created.versionNumber)} recuperada en un nuevo borrador`);
      await navigate({ to: "/o/$orgId/versions/$versionId/studio", params: { orgId, versionId: created.id } });
    } catch (problem) {
      notifyError(problem);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="grid gap-6">
      <PageHeader
        actions={
          fd?.capabilities.createCreative ? (
            <Button size="sm" disabled={restoring} onClick={() => void restore()}>
              Restaurar en nueva versión
            </Button>
          ) : null
        }
        eyebrow={
          <Link to={`/o/${orgId}/creatives/${v.creativeId}`} className="hover:underline">
            {creative.data?.name ?? "Creativo"}
          </Link>
        }
        title={
          <span className="flex items-center gap-2">
            Versión {v.versionNumber} <VersionStateBadge state={v.state} />
          </span>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <Section title="Flujo">
          <Panel className="p-4">
            <VersionPanel
              orgId={orgId}
              version={v}
              distributions={distributions.items}
              canCreateDistribution={fd?.capabilities.manageDistributions ?? false}
              product={creative.data?.product}
            />
          </Panel>
        </Section>
        <Section title="Diseño">
          <Panel className="grid gap-2 p-4 text-sm">
            <p>{summary}</p>
            <p className="text-xs text-muted">
              {v.editable
                ? "Editable en el Studio mientras esté en borrador."
                : "Esta versión es inmutable: para cambiarla crea una nueva versión."}
            </p>
            <Button asChild size="sm" variant={v.editable ? "primary" : "secondary"} className="justify-self-start">
              <Link to={`/o/${orgId}/versions/${v.id}/studio`}>
                {v.editable ? "Abrir en el Studio" : "Ver en el Studio"}
              </Link>
            </Button>
          </Panel>
        </Section>
      </div>
      <Section title="Historial">
        <Details
          items={[
            { label: "Creada", value: `${formatDateTime(v.createdAt)} · ${who(v.createdBy)}` },
            {
              label: "Enviada a revisión",
              value: v.submittedAt ? `${formatDateTime(v.submittedAt)} · ${who(v.submittedBy)}` : "—",
            },
            { label: "Aprobada", value: v.approvedAt ? `${formatDateTime(v.approvedAt)} · ${who(v.approvedBy)}` : "—" },
            {
              label: "Publicada",
              value: v.publishedAt ? `${formatDateTime(v.publishedAt)} · ${who(v.publishedBy)}` : "—",
            },
            {
              label: "Archivada",
              value: v.archivedAt ? `${formatDateTime(v.archivedAt)} · ${who(v.archivedBy)}` : "—",
            },
            {
              label: "Huella del contenido",
              value: <code className="font-mono text-xs">{v.contentHash.slice(0, 16)}…</code>,
            },
          ]}
        />
      </Section>
      {DevContentEditor ? (
        <Suspense fallback={null}>
          <Callout tone="warning" title="Herramienta de desarrollo">
            Solo visible en entornos de desarrollo; no existe en la aplicación publicada.
          </Callout>
          <DevContentEditor orgId={orgId} version={v} />
        </Suspense>
      ) : null}
    </div>
  );
}
